import { useEffect, useState, useCallback, useRef } from "react";
import { useOutletContext, useSearchParams } from "react-router-dom";
import { Search, SlidersHorizontal, ChevronLeft, ChevronRight, Phone } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { minDelay } from "@/lib/ux";
import { ErrorView, EmptyState } from "@/components/StateViews";
import { TableSkeleton } from "@/components/Skeletons";
import { QualityChip, StageChip, SlaChip } from "@/components/Chips";
import FilterChips from "@/components/FilterChips";
import FilterPanel from "@/components/FilterPanel";
import DateChips, { computeDateRange, detectDateChip } from "@/components/DateChips";
import BulkActionBar from "@/components/BulkActionBar";
import LeadDrawer from "@/components/LeadDrawer";
import PageSizeSelector from "@/components/PageSizeSelector";
import { useAuth } from "@/contexts/AuthContext";
import { fmtDate, fmtRelative, isOverdue, initials, fmtINR } from "@/lib/format";

const FILTER_KEYS = [
  "stage", "quality", "owner_user_id", "service_interested", "budget_range",
  "business_segment", "business_category", "platform", "campaign_name",
  "created_from", "created_to", "follow_up_status", "assigned", "status_filter",
  // additive: applied via dashboard click-through (no panel control needed)
  "contacted", "assigned_today", "no_next_followup", "has_deal", "sla", "active_scope",
];
const LEADS_FILTER_FIELDS = [
  "stage", "quality", "service_interested", "budget_range", "business_segment",
  "business_category", "owner_user_id", "assigned", "follow_up_status",
  "platform", "campaign_name", "created",
];
const blankFilters = () => { const f = Object.fromEntries(FILTER_KEYS.map((k) => [k, ""])); f.status_filter = "active"; return f; };

// Count of meaningful applied filters — ignores default status & counts a date
// range as a single filter (created_to folded into created_from).
const countFilters = (f) => {
  let n = 0;
  Object.entries(f).forEach(([k, v]) => {
    if (!v) return;
    if (k === "status_filter" && v === "active") return;
    if (k === "created_to") return;
    n++;
  });
  return n;
};

const STATUS_TABS = [
  { key: "active", label: "Active" },
  { key: "junk", label: "Junk" },
  { key: "lost", label: "Lost" },
  { key: "all", label: "All" },
];

const FOLLOWUP_LABELS = { overdue: "Overdue", today: "Due today", upcoming: "Upcoming", none: "No follow-up" };
const KEY_LABELS = {
  stage: "Stage", quality: "Quality", owner_user_id: "Owner", service_interested: "Service",
  budget_range: "Budget", business_segment: "Segment", business_category: "Category",
  platform: "Platform", campaign_name: "Campaign", created_from: "From", created_to: "To",
  follow_up_status: "Follow-up", assigned: "Assignment",
};

export default function Leads() {
  const { refreshKey, users } = useOutletContext();
  const { options, user } = useAuth();
  const [params, setParams] = useSearchParams();

  const initFilters = () => {
    const f = blankFilters();
    FILTER_KEYS.forEach((k) => { const v = params.get(k); if (v) f[k] = v; });
    return f;
  };  const [search, setSearch] = useState(params.get("search") || "");
  const [applied, setApplied] = useState(initFilters);   // drives load + chips + URL
  const [draft, setDraft] = useState(applied);            // panel edits, committed on Apply
  const [showFilters, setShowFilters] = useState(false);
  const [page, setPage] = useState(Number(params.get("page")) || 1);
  const [pageSize, setPageSize] = useState(25);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(params.get("open") || null);
  const [picked, setPicked] = useState(() => new Set());
  const [dateChip, setDateChip] = useState(() => detectDateChip(initFilters().created_from));
  const debounceRef = useRef();

  // Open a lead drawer when deep-linked via ?open=<id> (e.g. from global search).
  useEffect(() => {
    const openId = params.get("open");
    if (openId) setSelected(openId);
  }, [params]);

  // Keep the in-page search in sync with the URL (global search → /leads?search=…).
  useEffect(() => {
    const urlSearch = params.get("search") || "";
    setSearch((prev) => (prev === urlSearch ? prev : urlSearch));
  }, [params]);

  // Sync the APPLIED state to the URL (shareable/bookmarkable views).
  useEffect(() => {
    const next = {};
    FILTER_KEYS.forEach((k) => { if (applied[k]) next[k] = applied[k]; });
    if (search) next.search = search;
    if (page > 1) next.page = String(page);
    setParams(next, { replace: true });
  }, [applied, search, page, setParams]);

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const cleaned = Object.fromEntries(Object.entries(applied).filter(([, v]) => v));
      const { data } = await minDelay(api.get("/leads", { params: { page, page_size: pageSize, search: search || undefined, ...cleaned } }));
      setData(data.data);
    } catch (e) { setError(apiError(e)); }
    finally { setLoading(false); }
  }, [page, pageSize, search, applied]);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(load, search ? 350 : 0);
    return () => clearTimeout(debounceRef.current);
  }, [load, search]);

  useEffect(() => { load(); }, [refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { setPicked(new Set()); }, [page, search, applied]);

  const togglePick = (id) => setPicked((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const allOnPagePicked = !!data?.items.length && data.items.every((l) => picked.has(l.id));
  const toggleAll = () => setPicked(() => allOnPagePicked ? new Set() : new Set((data?.items || []).map((l) => l.id)));

  // ---- Filter panel (draft) actions ----
  const setDraftFilter = (k, v) => {
    if (k === "created_from" || k === "created_to") setDateChip(null); // manual edit clears quick-date chip
    setDraft((f) => {
      const next = { ...f, [k]: v };
      if (k === "business_segment") next.business_category = "";
      return next;
    });
  };
  const openFilters = () => { setDraft(applied); setShowFilters(true); };
  const applyFilters = () => { setApplied(draft); setPage(1); setShowFilters(false); };
  const resetDraft = () => setDraft(blankFilters());
  const cancelFilters = () => { setDraft(applied); setShowFilters(false); };
  const applySaved = (saved) => { const f = { ...blankFilters(), ...saved }; setApplied(f); setDraft(f); setPage(1); setDateChip(detectDateChip(f.created_from)); };

  const removeChip = (key) => {
    const next = { ...applied, [key]: "" };
    if (key === "created_from" || key === "created_to") { next.created_from = ""; next.created_to = ""; setDateChip(null); }
    setApplied(next); setDraft(next); setPage(1);
  };
  const clearAllChips = () => { setApplied(blankFilters()); setDraft(blankFilters()); setPage(1); setDateChip(null); };

  const draftCount = countFilters(draft);
  const appliedCount = countFilters(applied);
  const dirty = JSON.stringify(draft) !== JSON.stringify(applied);

  const chipLabel = (key, val) => {
    if (key === "owner_user_id") return `Owner: ${users?.find((u) => u.id === val)?.name || val}`;
    if (key === "assigned") return val === "true" ? "Assigned" : "Unassigned";
    if (key === "follow_up_status") return `Follow-up: ${FOLLOWUP_LABELS[val] || val}`;
    if (key === "stage") return `Stage: ${options?.stage_meta?.[val]?.name || val}`;
    if (key === "quality") return `Quality: ${String(val).split(",").join(", ")}`;
    if (key === "contacted") return val === "no" ? "Not contacted" : "Contacted";
    if (key === "assigned_today") return "Assigned today";
    if (key === "no_next_followup") return "No next follow-up";
    if (key === "has_deal") return val === "no" ? "No deal" : "Has deal";
    if (key === "sla") return "SLA breached";
    return `${KEY_LABELS[key] || key}: ${val}`;
  };
  const chips = FILTER_KEYS.filter((k) => k !== "status_filter" && k !== "active_scope" && applied[k]).map((k) => ({ key: k, label: chipLabel(k, applied[k]) }));

  // ---- Status tabs + date quick chips (apply immediately) ----
  const setStatus = (s) => { setApplied((f) => ({ ...f, status_filter: s })); setDraft((f) => ({ ...f, status_filter: s })); setPage(1); };
  const setDateRange = (days, key) => {
    const { from, to } = computeDateRange(days);
    const next = { ...applied, created_from: from, created_to: to };
    setApplied(next); setDraft((d) => ({ ...d, created_from: from, created_to: to })); setPage(1); setDateChip(key);
  };
  const clearDates = () => { const next = { ...applied, created_from: "", created_to: "" }; setApplied(next); setDraft((d) => ({ ...d, created_from: "", created_to: "" })); setPage(1); setDateChip(null); };
  const hasDateFilter = !!(applied.created_from || applied.created_to);

  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.page_size)) : 1;

  return (
    <div className="space-y-4" data-testid="leads-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-sora text-3xl font-bold text-navy">Leads</h1>
          <p className="text-sm text-ink/60">{data ? `${data.total} leads` : "Loading…"}{user?.role === "Sales Executive" ? " · assigned to you" : ""}</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink/40" />
            <input data-testid="leads-search" value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} placeholder="Search name, phone, campaign…" className="h-10 w-64 rounded-xl border border-sky bg-white pl-9 pr-3 text-sm outline-none focus:border-loop focus:ring-2 focus:ring-loop/20" />
          </div>
          <button onClick={() => showFilters ? cancelFilters() : openFilters()} data-testid="leads-filter-toggle" className="relative flex h-10 items-center gap-2 rounded-xl border border-sky bg-white px-3 text-sm font-medium text-navy hover:bg-mist">
            <SlidersHorizontal className="h-4 w-4" /> Filters
            {appliedCount > 0 && <span className="rounded-full bg-loop px-1.5 text-[11px] font-bold text-white">{appliedCount}</span>}
          </button>
        </div>
      </div>

      <div className="relative space-y-4">

        {/* Status tabs + quick date chips */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap gap-1.5" data-testid="leads-status-tabs">
            {STATUS_TABS.map((t) => (
              <button key={t.key} data-testid={`status-tab-${t.key}`} onClick={() => setStatus(t.key)}
                className={`rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${(applied.status_filter || "active") === t.key ? "bg-navy text-white" : "border border-sky bg-white text-ink/60 hover:bg-mist"}`}>
                {t.label}
              </button>
            ))}
          </div>
          <DateChips selected={dateChip} onPick={setDateRange} onClear={clearDates} hasDates={hasDateFilter} testid="leads-date-chips" />
        </div>

        {showFilters && (
          <FilterPanel
            module="leads"
            fields={LEADS_FILTER_FIELDS}
            options={options} users={users} user={user}
            draft={draft} applied={applied}
            appliedCount={appliedCount} draftCount={draftCount} dirty={dirty}
            onChange={setDraftFilter} onApply={applyFilters} onReset={resetDraft} onCancel={cancelFilters} onApplySaved={applySaved}
          />
        )}

      <FilterChips chips={chips} onRemove={removeChip} onClearAll={clearAllChips} testid="leads-chips" />

      {loading ? <TableSkeleton rows={8} cols={user?.role === "Sales Executive" ? 9 : 10} /> : error ? <ErrorView message="Couldn't load leads. Please try again." onRetry={load} /> :
        !data?.items.length ? (
          <EmptyState title="No leads match this filter" subtitle="Try adjusting or clearing your filters, or import leads from the Import Center." />
        ) : (
          <>
            <div className="hidden overflow-hidden rounded-2xl border border-sky/70 bg-white lg:block">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-mist text-left text-xs font-semibold uppercase tracking-wide text-ink/50">
                    <tr>
                      <th className="w-10 px-4 py-3">
                        <input type="checkbox" data-testid="leads-select-all" checked={allOnPagePicked} onChange={toggleAll} className="h-4 w-4 rounded border-sky text-loop" />
                      </th>
                      {["Lead", "Phone", "Quality", "Stage", "Owner", "Service", "Budget", "Deal", "Campaign", "Created", "Next Follow-up"].map((h) => (
                        <th key={h} className="whitespace-nowrap px-4 py-3">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-sky/50">
                    {data.items.map((l) => (
                      <tr key={l.id} data-testid={`lead-row-${l.id}`} onClick={() => setSelected(l.id)} className={`cursor-pointer transition-colors hover:bg-sky/20 ${picked.has(l.id) ? "bg-loop/5" : ""}`}>
                        <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                          <input type="checkbox" data-testid={`lead-check-${l.id}`} checked={picked.has(l.id)} onChange={() => togglePick(l.id)} className="h-4 w-4 rounded border-sky text-loop" />
                        </td>
                        <td className="px-4 py-3">
                          <p className="font-medium text-navy">{l.name || "—"}</p>
                          <div className="flex items-center gap-1.5">
                            <p className="text-xs text-ink/50">{l.business_name || l.email || "—"}</p>
                            <SlaChip sla={l.sla} />
                          </div>
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 text-ink/70">{l.phone || "—"}</td>
                        <td className="px-4 py-3"><QualityChip value={l.quality} /></td>
                        <td className="px-4 py-3"><StageChip value={l.stage} /></td>
                        <td className="whitespace-nowrap px-4 py-3">{l.owner_name ? <span className="flex items-center gap-1.5"><span className="flex h-6 w-6 items-center justify-center rounded-md bg-navy text-[10px] font-bold text-white">{initials(l.owner_name)}</span>{l.owner_name}</span> : <span className="text-amber-600">Unassigned</span>}</td>
                        <td className="whitespace-nowrap px-4 py-3 text-ink/70">{l.service_interested || "—"}</td>
                        <td className="whitespace-nowrap px-4 py-3 text-ink/70">{l.budget_range || "—"}</td>
                        <td className="whitespace-nowrap px-4 py-3" data-testid={`lead-deal-${l.id}`}>{l.deal_amount != null ? <span className="font-semibold text-emerald-600">{fmtINR(l.deal_amount)}</span> : <span className="text-slate-300">—</span>}</td>
                        <td className="whitespace-nowrap px-4 py-3 text-ink/70">{l.campaign_name || "—"}</td>
                        <td className="whitespace-nowrap px-4 py-3 text-ink/60">{fmtDate(l.created_at)}</td>
                        <td className="whitespace-nowrap px-4 py-3">{l.next_follow_up_at ? <span className={isOverdue(l.next_follow_up_at) ? "font-medium text-red-500" : "text-ink/70"}>{fmtRelative(l.next_follow_up_at)}</span> : <span className="text-slate-300">—</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="space-y-3 lg:hidden">
              {data.items.map((l) => (
                <div key={l.id} data-testid={`lead-card-${l.id}`} onClick={() => setSelected(l.id)} className={`rounded-2xl border bg-white p-4 ${picked.has(l.id) ? "border-loop ring-1 ring-loop/30" : "border-sky/70"}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-2.5">
                      <input type="checkbox" data-testid={`lead-check-${l.id}`} onClick={(e) => e.stopPropagation()} checked={picked.has(l.id)} onChange={() => togglePick(l.id)} className="mt-1 h-4 w-4 rounded border-sky text-loop" />
                      <div>
                        <p className="font-sora font-semibold text-navy">{l.name || "—"}</p>
                        <p className="text-xs text-ink/50">{l.business_name || l.email}</p>
                      </div>
                    </div>
                    <QualityChip value={l.quality} />
                  </div>
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <StageChip value={l.stage} />
                    <SlaChip sla={l.sla} />
                    {l.phone && <a href={`tel:${l.phone}`} onClick={(e) => e.stopPropagation()} className="flex items-center gap-1 text-xs text-loop"><Phone className="h-3 w-3" />{l.phone}</a>}
                  </div>
                  <div className="mt-2 flex items-center justify-between text-xs text-ink/50">
                    <span>{l.owner_name || "Unassigned"}</span>
                    <div className="flex items-center gap-2">
                      {l.deal_amount != null && <span className="font-semibold text-emerald-600">{fmtINR(l.deal_amount)}</span>}
                      {l.next_follow_up_at && <span className={isOverdue(l.next_follow_up_at) ? "text-red-500" : ""}>{fmtRelative(l.next_follow_up_at)}</span>}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex items-center justify-between">
              <p className="text-sm text-ink/50">Page {data.page} of {totalPages}</p>
              <div className="flex items-center gap-3">
                <PageSizeSelector value={pageSize} onChange={(n) => { setPageSize(n); setPage(1); }} testid="leads-page-size" />
                <div className="flex gap-2">
                  <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="leads-prev" className="flex items-center gap-1 rounded-lg border border-sky bg-white px-3 py-2 text-sm font-medium text-navy disabled:opacity-40"><ChevronLeft className="h-4 w-4" />Prev</button>
                  <button disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} data-testid="leads-next" className="flex items-center gap-1 rounded-lg border border-sky bg-white px-3 py-2 text-sm font-medium text-navy disabled:opacity-40">Next<ChevronRight className="h-4 w-4" /></button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      <LeadDrawer leadId={selected} open={!!selected} onOpenChange={(v) => { if (!v) { setSelected(null); if (params.get("open")) { const n = new URLSearchParams(params); n.delete("open"); setParams(n, { replace: true }); } } }} onUpdated={load} users={users} />
      <BulkActionBar
        selectedIds={Array.from(picked)}
        users={users}
        onClear={() => setPicked(new Set())}
        onDone={() => { setPicked(new Set()); load(); }}
      />
    </div>
  );
}
